import { configuration } from "./Configuration";
import { syncFileSession } from './fileSession';
import { requestSignal } from "./utilities/requestTimeout";
import { noteSsoSessionEnd } from './utilities/entraSso';
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { currentPathForRedirect, loginPathWithTenant } from "./utilities/loginRedirect";
import i18next from "i18next";

export interface JWTTokens {
  accessToken: string;
  refreshToken: string;
}

export interface JWTAuthService {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(tokens: JWTTokens): void;
  clearTokens(): void;
  isTokenExpired(token: string): boolean;
  refreshAccessToken(): Promise<string | null>;
  getAuthHeaders(): Record<string, string>;
}

// Function to populate authentication from JWT token
const populateAuthenticationFromToken = (accessToken: string | null) => {
  if (!accessToken) return null;
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) return null;

    // Base64URL to Base64
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // Add padding
    while (base64.length % 4) base64 += "=";

    // Decode with UTF-8 support safely
    const jsonPayload = decodeURIComponent(
      Array.prototype.map
        .call(window.atob(base64), (c: string) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    const payload = JSON.parse(jsonPayload);
    if (payload) {
      const auth = {
        customer: payload.customer || payload.customerId || payload.sub,
        user: payload.user || payload.userId || payload.sub,
        id: payload.id || payload._id || payload.sub,
        email: payload.email,
        roleAssignments: payload.roleAssignments || [],
        roles: payload.roles || [],
        firstName: payload.firstName,
        lastName: payload.lastName,
        mobileNumber: payload.mobileNumber,
        authenticatorAppEnabled: payload.authenticatorAppEnabled,
      };

      // Sync to storage so Jotai atoms pick it up
      localStorage.setItem("tri.authentication", JSON.stringify(auth));
      return auth;
    }
  } catch (error) {
    console.error("Error parsing JWT token:", error);
  }
  return null;
};

class JWTAuthServiceImpl implements JWTAuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    console.log("JWTAuthService constructor called"); // Debug log
    // Load tokens from localStorage on initialization
    this.loadTokensFromStorage();


    // Populate authentication if tokens exist
    if (this.accessToken) {
      console.log("Calling populateAuthenticationFromToken"); // Debug log
      populateAuthenticationFromToken(this.accessToken);
      // Eine schon bestehende Anmeldung bekommt ihr Datei-Cookie beim Start (siehe fileSession.ts).
      syncFileSession(this.accessToken);
    } else {
      console.log("No access token found, skipping authentication population"); // Debug log
    }
  }

  private loadTokensFromStorage(): void {
    try {
      const stored = localStorage.getItem("tri.jwt.tokens");
      if (stored) {
        const tokens = JSON.parse(stored);
        this.accessToken = tokens.accessToken || null;
        this.refreshToken = tokens.refreshToken || null;
      }
    } catch (error) {
      console.error("Failed to load JWT tokens from storage:", error);
    }
  }

  private saveTokensToStorage(): void {
    try {
      const tokens = {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
      };
      localStorage.setItem("tri.jwt.tokens", JSON.stringify(tokens));
    } catch (error) {
      console.error("Failed to save JWT tokens to storage:", error);
    }
  }

  getAccessToken(): string | null {
    if (!this.accessToken) {
      this.loadTokensFromStorage();
    }
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    if (this.refreshToken) {
      return this.refreshToken;
    }
    this.loadTokensFromStorage();
    return this.refreshToken;
  }

  setTokens(tokens: JWTTokens): void {
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;
    this.saveTokensToStorage();

    // Populate authentication atom from the new token
    populateAuthenticationFromToken(this.accessToken);
    syncFileSession(this.accessToken);
    
    // Clear tenant-specific caches when session changes
    if (typeof window !== 'undefined') {
      // Dynamic import to avoid circular dependency
      import('./portfolio-dashboard/phaseParcelsApi').then(({ invalidatePhaseParcels }) => {
        invalidatePhaseParcels();
      }).catch(() => {
        // Module may not be loaded yet, ignore
      });
    }
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem("tri.jwt.tokens");
    localStorage.removeItem("tri.authentication");
    syncFileSession(null);
    
    // Clear tenant-specific caches to prevent session leakage
    if (typeof window !== 'undefined') {
      // Dynamic import to avoid circular dependency
      import('./portfolio-dashboard/phaseParcelsApi').then(({ invalidatePhaseParcels }) => {
        invalidatePhaseParcels();
      }).catch(() => {
        // Module may not be loaded yet, ignore
      });
    }
  }

  isTokenExpired(token: string): boolean {
    try {
      // Check if token has the expected JWT format (3 parts separated by dots)
      if (
        !token ||
        typeof token !== "string" ||
        token.split(".").length !== 3
      ) {
        console.warn("Invalid JWT token format:", token);
        return true;
      }

      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error("Error parsing JWT token:", error);
      return true; // Consider expired if we can't parse it
    }
  }

  decodeToken(token: string): any {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    } catch (error) {
      console.error("Error parsing JWT token:", error);
      return null;
    }
  }

  async refreshAccessToken(): Promise<string | null> {
    if (!this.refreshToken) {
      return null;
    }

    try {
      const response = await fetch(
        `${configuration.serverUrl}/tokens/refresh`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.refreshToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        this.accessToken = data.accessToken;
        this.saveTokensToStorage();
        syncFileSession(this.accessToken);
        return this.accessToken;
      } else {
        // SSO only: say why on the login page (tri-server docs/entra-sso.md, "Nur SSO").
        noteSsoSessionEnd((await response.json().catch(() => null))?.error);
        // Refresh token is invalid, clear all tokens
        this.clearTokens();
        return null;
      }
    } catch (error) {
      console.error("Failed to refresh access token:", error);
      this.clearTokens();
      return null;
    }
  }

  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    // Add locale information for server-side internationalization
    const locale = i18next.resolvedLanguage || "de";
    headers["Accept-Language"] = locale;

    return headers;
  }
}

export const jwtAuthService = new JWTAuthServiceImpl();

// Function to get authentication from JWT token - can be called from components
export const getAuthenticationFromToken = () => {
  const accessToken = jwtAuthService.getAccessToken();
  if (accessToken) {
    return populateAuthenticationFromToken(accessToken);
  }
  return null;
};

// Helper function to make authenticated API calls with automatic token refresh
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {},
  // When true, a persistent 401 (after refresh) is returned to the caller
  // instead of redirecting the whole app to /login. Use for calls that proxy an
  // EXTERNAL resource (e.g. an external MCP server's OAuth 401), where a 401
  // means "that resource needs auth", not "your session expired".
  suppressAuthRedirect = false,
): Promise<any> {
  const accessToken = jwtAuthService.getAccessToken();

  // If we have a JWT token, use JWT authentication
  if (accessToken) {
    // Add JWT headers to the request
    const authHeaders = jwtAuthService.getAuthHeaders();

    // For FormData, only include Authorization header, let browser handle Content-Type
    const headers: Record<string, string> = {};
    if (options.body instanceof FormData) {
      // Only include Authorization for FormData, let browser set Content-Type
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }
    } else {
      // For non-FormData, include all headers including Content-Type
      Object.assign(headers, authHeaders);
      headers["Content-Type"] = "application/json";
    }

    // Add Accept-Language header based on current i18n language
    const currentLanguage = i18next.language || "de";
    headers["Accept-Language"] = currentLanguage;

    Object.assign(headers, options.headers);

    /*
     * Mit einer Zeitgrenze, wo keine mitgebracht wurde.
     *
     * Ohne sie belegt eine Anfrage, die nie beantwortet wird, für immer eine Verbindung. Ein
     * Browser hält je Host nur etwa sechs offen — sind sie vergeben, wartet alles Weitere, ohne
     * dass irgendwo ein Fehler erschiene: Die Seite steht auf Ladeskeletten, der Hauptthread
     * rechnet nichts, die Konsole bleibt still. Eine Grenze macht daraus einen Fehler, und ein
     * Fehler ist behandelbar. Uploads und Datenströme sind ausgenommen; siehe `requestSignal`.
     */
    const signal = requestSignal(options);

    // Make the initial request
    let response = await fetch(url, {
      ...options,
      headers,
      signal,
    });

    // If we get a 401 and have a refresh token, try to refresh
    if (response.status === 401 && jwtAuthService.getRefreshToken()) {
      const newAccessToken = await jwtAuthService.refreshAccessToken();

      if (newAccessToken) {
        // Retry with the new token and every header the first attempt carried. Rebuilt from the
        // auth headers alone, the retry lost `Content-Type`: a JSON body went out as text/plain
        // and the server read an empty body (TRI-SERVER-6Q/-6R).
        const newHeaders: Record<string, string> = {
          ...headers,
          Authorization: `Bearer ${newAccessToken}`,
        };
        // Dieselbe Frist wie beim ersten Versuch: Sie gilt der Handlung, nicht dem Versuch — sonst
        // verdoppelte eine Erneuerung des Tokens die Zeit, die eine verlorene Anfrage kostet.
        response = await fetch(url, {
          ...options,
          headers: newHeaders,
          signal,
        });
      }
    }

    if (response.status !== 401) {
      return response;
    }
    // Persistent 401 even after refresh. A caller proxying an external resource
    // can opt out of the global login redirect and handle the 401 itself — but
    // only when the portal session is still alive. If the JWT is expired/invalid
    // or refresh failed, fall through to the login redirect below.
    if (suppressAuthRedirect) {
      const currentAccessToken = jwtAuthService.getAccessToken();
      if (
        currentAccessToken &&
        !jwtAuthService.isTokenExpired(currentAccessToken)
      ) {
        return response;
      }
    }
  }
  // No valid JWT token available - redirect to login page (keep tenant branding).
  if (!window.location.pathname.startsWith("/login")) {
    console.log("No JWT token available, redirecting to login for:", url);
    // The target first: it reads the tenant from the stored authentication.
    const target = loginPathWithTenant(currentPathForRedirect());
    // End the session before leaving. The login page sends anyone with a stored authentication straight
    // back into the app, so a token the server keeps refusing (a refresh that still says `mfaRequired`)
    // bounced between /login and the app without end.
    jwtAuthService.clearTokens();
    window.location.assign(target);
  }
  return;
}

// Helper function to handle API responses with proper error handling
export async function handleAuthenticatedResponse<T>(
  response: Response,
  callback?: (data: any) => T,
): Promise<T> {
  console.log(
    "handleAuthenticatedResponse: starting, ok:",
    response.ok,
    "status:",
    response.status,
  );
  if (response.ok) {
    const result = await response.json();
    console.log("handleAuthenticatedResponse: json parsed");
    if (callback) {
      return callback(result);
    }
    return result as T;
  } else {
    if (response.status === 400) {
      throw new ApplicationError((await response.json()).message, 400);
    } else if (response.status === 401) {
      // Only the *parse* may fail here. The auth error used to be thrown from inside this `try`,
      // where its own `catch` caught it and replaced it with a plain UnauthorizedError — so
      // `passwordChangeRequired`/`mfaRequired` never reached the caller and the branches waiting
      // for them in LoginPage and ModalProvider could not fire. A forced password change looked
      // like an ordinary rejected login.
      let errorData: any;
      try {
        errorData = await response.json();
      } catch {
        // No parsable body — nothing to distinguish, so the standard 401.
        throw new UnauthorizedError();
      }

      if (
        errorData &&
        typeof errorData === "object" &&
        (errorData.passwordChangeRequired || errorData.mfaRequired)
      ) {
        console.log(
          "Authentication requirement detected in 401 response:",
          errorData,
        );
        // Create a custom error with authentication flags
        const authError = new Error("Authentication required") as any;
        authError.passwordChangeRequired = errorData.passwordChangeRequired;
        authError.mfaRequired = errorData.mfaRequired;
        authError.status = 401;
        throw authError;
      }

      throw new UnauthorizedError();
    } else {
      const error = new Error(
        `API request failed with status ${response.status}`,
      ) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
  }
}
